import { resolveEstimateRate, resolveRegionPricingConfig, type EstimateRateType } from './estimate.rates';
import { roundMoney } from './money';

export type QuickScopeRateId =
  | 'painting'
  | 'plaster_painting'
  | 'scrape_prime_plaster_paint'
  | 'panels'
  | 'bathroom'
  | 'paving'
  | 'other';

export type ScopeRateComponent = {
  id: EstimateRateType | 'panels';
  label: string;
  rateMin: number;
  rateDefault: number;
  rateMax: number;
  unit: 'zł/m²';
};

export type ScopeRateSuggestion = {
  rate: number;
  rateMin: number;
  rateMax: number;
  unit: 'zł/m²';
  regionLabel: string;
  scopeDescription: string;
  note: string;
  components: ScopeRateComponent[];
};

export const QUICK_SCOPE_RATE_LABEL = 'Orientacyjna stawka robocizny';

export function getPrimaryRateLabel(): string {
  return QUICK_SCOPE_RATE_LABEL;
}

function getRegionMultiplier(region: string): number {
  return resolveRegionPricingConfig(region).multiplier;
}

function getRegionLabel(region: string): string {
  return resolveRegionPricingConfig(region).label;
}

function applyRegionMultiplier(rate: number, region: string): number {
  return roundMoney(rate * getRegionMultiplier(region));
}

function component(
  id: ScopeRateComponent['id'],
  label: string,
  range: { min: number; default: number; max: number },
  region: string
): ScopeRateComponent {
  return {
    id,
    label,
    rateMin: applyRegionMultiplier(range.min, region),
    rateDefault: applyRegionMultiplier(range.default, region),
    rateMax: applyRegionMultiplier(range.max, region),
    unit: 'zł/m²',
  };
}

function rateComponent(
  type: EstimateRateType,
  label: string,
  region: string,
  range?: { min: number; max: number }
): ScopeRateComponent {
  const rateDefault = resolveEstimateRate(type, region);

  return {
    id: type,
    label,
    rateMin: range ? applyRegionMultiplier(range.min, region) : Math.max(0, roundMoney(rateDefault * 0.85)),
    rateDefault,
    rateMax: range ? applyRegionMultiplier(range.max, region) : roundMoney(rateDefault * 1.25),
    unit: 'zł/m²',
  };
}

function buildSuggestion(
  scopeDescription: string,
  region: string,
  components: ScopeRateComponent[],
  note: string
): ScopeRateSuggestion {
  return {
    rate: components.reduce((sum, item) => sum + item.rateDefault, 0),
    rateMin: components.reduce((sum, item) => sum + item.rateMin, 0),
    rateMax: components.reduce((sum, item) => sum + item.rateMax, 0),
    unit: 'zł/m²',
    regionLabel: getRegionLabel(region),
    scopeDescription,
    note,
    components,
  };
}

export function getScopeRateSuggestion(scope: QuickScopeRateId, region: string): ScopeRateSuggestion {
  if (scope === 'plaster_painting') {
    return buildSuggestion(
      'gładź + malowanie',
      region,
      [
        rateComponent('plaster', 'Wykonanie gładzi z obróbką i szlifowaniem', region, { min: 50, max: 60 }),
        rateComponent('painting', 'Malowanie 2x', region, { min: 25, max: 35 }),
      ],
      'W tym: gładź z obróbką i szlifowaniem oraz malowanie 2x. Materiały wybierasz osobno.'
    );
  }

  if (scope === 'scrape_prime_plaster_paint') {
    return buildSuggestion(
      'pełne przygotowanie + wykończenie',
      region,
      [
        // Same rate source as the engine item, so the suggested rate and the
        // priced position can never drift apart.
        rateComponent('scraping', 'Skrobanie / przygotowanie podłoża', region, { min: 10, max: 18 }),
        rateComponent('primer', 'Gruntowanie', region, { min: 6, max: 10 }),
        rateComponent('plaster', 'Wykonanie gładzi z obróbką i szlifowaniem', region, { min: 50, max: 60 }),
        rateComponent('painting', 'Malowanie 2x', region, { min: 25, max: 35 }),
      ],
      'To zakres z dodatkowymi pracami przygotowawczymi. Stawka jest sumą pozycji roboczych, nie jedną ukrytą liczbą.'
    );
  }

  if (scope === 'bathroom') {
    return buildSuggestion(
      'łazienka / płytki',
      region,
      [rateComponent('bath_tiles', 'Układanie płytek', region)],
      'Szybka wycena płytek nie zastępuje pełnego kosztorysu łazienki.'
    );
  }

  if (scope === 'paving') {
    return buildSuggestion(
      'bruk / kostka',
      region,
      [rateComponent('paving_laying', 'Układanie kostki', region)],
      'Stawka dotyczy robocizny układania. Podbudowa i transport wymagają osobnego sprawdzenia.'
    );
  }

  if (scope === 'panels') {
    return buildSuggestion(
      'panele / zakres do potwierdzenia',
      region,
      [component('panels', 'Robocizna paneli', { min: 35, default: 45, max: 60 }, region)],
      'Zakres paneli w szybkiej wycenie jest orientacyjny i wymaga potwierdzenia.'
    );
  }

  if (scope === 'other') {
    return buildSuggestion(
      'własny opis - stawka do ręcznego sprawdzenia',
      region,
      [rateComponent('painting', 'Stawka robocza do potwierdzenia', region, { min: 25, max: 35 })],
      'Przy własnym zakresie potraktuj stawkę jako punkt startowy i potwierdź ją ręcznie.'
    );
  }

  return buildSuggestion(
    'malowanie',
    region,
    [rateComponent('painting', 'Malowanie 2x', region, { min: 25, max: 35 })],
    'Robocizna dotyczy malowania 2x. Grunt, zabezpieczenia i materiały sprawdzasz osobno.'
  );
}

export function formatScopeRateBreakdown(components: ScopeRateComponent[]): string {
  return components
    .map((item) => `${item.label}: ${String(item.rateDefault)} ${item.unit}`)
    .join(' + ');
}
